import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import useSWR from "swr";
import api from "./config/Api";
import Swal from "sweetalert2";
import { Auth } from "./utils/Auth";

import ProtectedRoute from "./components/ProtectedRoute";
import LoadingPage from "./pages/Loading";

// Lazy load pages
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const RoleManagement = lazy(() => import("./pages/RoleManagement"));
const Pages = lazy(() => import("./pages/Pages"));
const Institutions = lazy(() => import("./pages/Institutions"));
const Groups = lazy(() => import("./pages/Groups"));
const CategoryProfile = lazy(() => import("./pages/CategoryProfile"));
const CategoryInformation = lazy(() => import("./pages/CategoryInformation"));
const PublicInformation = lazy(() => import("./pages/PublicInformation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/Profile"));
const TypeInformation = lazy(() => import("./pages/TypeInformation"));
const Report = lazy(() => import("./pages/Report"));
const OrderReport = lazy(() => import("./pages/OrderReport"));
const SearchDokumen = lazy(() => import("./pages/SearchDokumen"));
const DocumentDetail = lazy(() => import("./pages/DocumentDetail"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const Slide = lazy(() => import("./pages/Slide"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

export const fetcher = async (url, body) => {
  const response = await api.post(url, body);
  return response.data.data;
};

const viewComponents = {
  Dashboard,
  UserManagement,
  RoleManagement,
  Pages,
  Institutions,
  Groups,
  CategoryProfile,
  CategoryInformation,
  PublicInformation,
  TypeInformation,
  Report,
  OrderReport,
  Slide,
};

const renderRoutesFromMenu = (pages) =>
  pages.flatMap((page) => {
    const routes = [];
    if (page.view_name && viewComponents[page.view_name]) {
      routes.push(
        <Route
          key={page.url}
          path={page.url}
          element={
            <ProtectedRoute>
              {React.createElement(viewComponents[page.view_name])}
            </ProtectedRoute>
          }
        />,
      );
    }
    if (page.children?.length) {
      routes.push(...renderRoutesFromMenu(page.children));
    }
    return routes;
  });

export default function App() {
  const rawUser = Auth.getUser();
  const user = rawUser ? rawUser : null;

  const shouldFetch = Boolean(user?.group_id);

  // Gunakan array sebagai key dan kirim body ke fetcher
  const { data: menuData, error } = useSWR(
    shouldFetch ? ["/api/menu", { group_id: user.group_id }] : null,
    ([url, body]) => fetcher(url, body),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "Gagal memuat menu. Anda akan dialihkan ke halaman login.",
        confirmButtonText: "OK",
      }).then(() => {
        Auth.logout();
      });
    }
  }, [error]);

  if (error) return null;
  if (shouldFetch && !menuData) return <LoadingPage />;

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/information" element={<SearchDokumen />} />
          <Route path="/information/:id" element={<DocumentDetail />} />
          <Route path="/profil" element={<ProfilePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {menuData && renderRoutesFromMenu(menuData ?? [])}

          <Route
            path="/Profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/report/:id"
            element={
              <ProtectedRoute>
                <Report />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-report/:id"
            element={
              <ProtectedRoute>
                <OrderReport />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
